import { JobCardDetails, JobCardStatus, JobCardQuantities } from '@/types/jobCards';
import { JobOperator } from '@/types/operators';

// Validation error types
export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// Role-based permissions
export type UserRole = 'operator' | 'supervisor' | 'manager' | 'admin' | 'quality_inspector';

export interface PermissionConfig {
  canStartJob: boolean;
  canPauseJob: boolean;
  canCompleteJob: boolean;
  canEditQuantities: boolean;
  canAssignOperators: boolean;
  canRemoveOperators: boolean;
  canRecordQC: boolean;
  canCreateNCR: boolean;
  canIssueAttachments: boolean;
  canRecordDefects: boolean;
  canViewAudit: boolean;
  canExportAudit: boolean;
}

// Role permissions mapping
const ROLE_PERMISSIONS: Record<UserRole, PermissionConfig> = {
  operator: {
    canStartJob: true,
    canPauseJob: true,
    canCompleteJob: true,
    canEditQuantities: true,
    canAssignOperators: false,
    canRemoveOperators: false,
    canRecordQC: false,
    canCreateNCR: false,
    canIssueAttachments: true,
    canRecordDefects: true,
    canViewAudit: false,
    canExportAudit: false,
  },
  supervisor: {
    canStartJob: true,
    canPauseJob: true,
    canCompleteJob: true,
    canEditQuantities: true,
    canAssignOperators: true,
    canRemoveOperators: true,
    canRecordQC: true,
    canCreateNCR: true,
    canIssueAttachments: true,
    canRecordDefects: true,
    canViewAudit: true,
    canExportAudit: false,
  },
  manager: {
    canStartJob: true,
    canPauseJob: true,
    canCompleteJob: true,
    canEditQuantities: true,
    canAssignOperators: true,
    canRemoveOperators: true,
    canRecordQC: true,
    canCreateNCR: true,
    canIssueAttachments: true,
    canRecordDefects: true,
    canViewAudit: true,
    canExportAudit: true,
  },
  admin: {
    canStartJob: true,
    canPauseJob: true,
    canCompleteJob: true,
    canEditQuantities: true,
    canAssignOperators: true,
    canRemoveOperators: true,
    canRecordQC: true,
    canCreateNCR: true,
    canIssueAttachments: true,
    canRecordDefects: true,
    canViewAudit: true,
    canExportAudit: true,
  },
  quality_inspector: {
    canStartJob: false,
    canPauseJob: false,
    canCompleteJob: false,
    canEditQuantities: false,
    canAssignOperators: false,
    canRemoveOperators: false,
    canRecordQC: true,
    canCreateNCR: true,
    canIssueAttachments: true,
    canRecordDefects: true,
    canViewAudit: true,
    canExportAudit: false,
  },
};

/**
 * Get permissions for a specific role
 */
export function getRolePermissions(role: UserRole): PermissionConfig {
  return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.operator;
}

/**
 * Check if user has permission for a specific action
 */
export function hasPermission(role: UserRole, action: keyof PermissionConfig): boolean {
  const permissions = getRolePermissions(role);
  return permissions[action];
}

/**
 * Validate job card status transition
 */
export function validateStatusTransition(
  currentStatus: JobCardStatus,
  newStatus: JobCardStatus,
  userRole: UserRole
): ValidationResult {
  const errors: ValidationError[] = [];

  // Define valid status transitions
  const validTransitions: Record<JobCardStatus, JobCardStatus[]> = {
    'Not Started': ['In Progress', 'Cancelled'],
    'In Progress': ['Paused', 'Completed', 'QC Hold', 'Done'],
    'Paused': ['In Progress', 'Cancelled'],
    'QC Hold': ['In Progress', 'Completed', 'Cancelled'],
    'Completed': ['QC Hold', 'Done'], // Allow re-opening for QC issues or finalizing
    'Done': [], // Terminal state
    'Cancelled': [], // Terminal state
  };

  // Check if transition is valid
  const allowedTransitions = validTransitions[currentStatus] || [];
  if (!allowedTransitions.includes(newStatus)) {
    errors.push({
      field: 'status',
      message: `Invalid status transition from ${currentStatus} to ${newStatus}`,
      code: 'INVALID_STATUS_TRANSITION',
    });
  }

  // Check role permissions for specific transitions
  if (newStatus === 'In Progress' && !hasPermission(userRole, 'canStartJob')) {
    errors.push({
      field: 'status',
      message: 'You do not have permission to start jobs',
      code: 'INSUFFICIENT_PERMISSIONS',
    });
  }

  if (newStatus === 'Paused' && !hasPermission(userRole, 'canPauseJob')) {
    errors.push({
      field: 'status',
      message: 'You do not have permission to pause jobs',
      code: 'INSUFFICIENT_PERMISSIONS',
    });
  }

  if (newStatus === 'Completed' && !hasPermission(userRole, 'canCompleteJob')) {
    errors.push({
      field: 'status',
      message: 'You do not have permission to complete jobs',
      code: 'INSUFFICIENT_PERMISSIONS',
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate job card quantities
 */
export function validateQuantities(
  quantities: JobCardQuantities,
  jobCard: JobCardDetails
): ValidationResult {
  const errors: ValidationError[] = [];

  // Check for negative quantities
  if (quantities.good < 0) {
    errors.push({
      field: 'good',
      message: 'Good quantity cannot be negative',
      code: 'NEGATIVE_QUANTITY',
    });
  }

  if (quantities.scrap < 0) {
    errors.push({
      field: 'scrap',
      message: 'Scrap quantity cannot be negative',
      code: 'NEGATIVE_QUANTITY',
    });
  }

  if (quantities.rework && quantities.rework < 0) {
    errors.push({
      field: 'rework',
      message: 'Rework quantity cannot be negative',
      code: 'NEGATIVE_QUANTITY',
    });
  }

  // Check total quantity against target
  const totalQuantity = quantities.good + quantities.scrap + (quantities.rework || 0);
  if (totalQuantity > jobCard.quantity) {
    errors.push({
      field: 'total',
      message: `Total quantity (${totalQuantity}) cannot exceed planned quantity (${jobCard.quantity})`,
      code: 'EXCEEDS_PLANNED_QUANTITY',
    });
  }

  // Check for decimal quantities if unit doesn't allow decimals (assuming most units are pieces)
  const isIntegerUnit = true; // For now, assume all quantities should be integers
  if (isIntegerUnit) {
    if (!Number.isInteger(quantities.good)) {
      errors.push({
        field: 'good',
        message: 'Good quantity must be a whole number for this unit',
        code: 'NON_INTEGER_QUANTITY',
      });
    }

    if (!Number.isInteger(quantities.scrap)) {
      errors.push({
        field: 'scrap',
        message: 'Scrap quantity must be a whole number for this unit',
        code: 'NON_INTEGER_QUANTITY',
      });
    }

    if (quantities.rework && !Number.isInteger(quantities.rework)) {
      errors.push({
        field: 'rework',
        message: 'Rework quantity must be a whole number for this unit',
        code: 'NON_INTEGER_QUANTITY',
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate operator assignment
 */
export function validateOperatorAssignment(
  operatorId: string,
  jobCard: JobCardDetails,
  existingOperators: JobOperator[],
  userRole: UserRole
): ValidationResult {
  const errors: ValidationError[] = [];

  // Check permission
  if (!hasPermission(userRole, 'canAssignOperators')) {
    errors.push({
      field: 'operator',
      message: 'You do not have permission to assign operators',
      code: 'INSUFFICIENT_PERMISSIONS',
    });
  }

  // Check if operator is already assigned
  if (existingOperators.some(op => op.operator_id === operatorId && op.status === 'Active')) {
    errors.push({
      field: 'operator',
      message: 'Operator is already assigned to this job card',
      code: 'OPERATOR_ALREADY_ASSIGNED',
    });
  }

  // Check job card status
  if (!['Not Started', 'In Progress', 'Paused'].includes(jobCard.status)) {
    errors.push({
      field: 'status',
      message: 'Cannot assign operators to jobs that are completed or cancelled',
      code: 'INVALID_JOB_STATUS',
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate operator removal
 */
export function validateOperatorRemoval(
  jobOperatorId: string,
  jobCard: JobCardDetails,
  existingOperators: JobOperator[],
  userRole: UserRole
): ValidationResult {
  const errors: ValidationError[] = [];

  // Check permission
  if (!hasPermission(userRole, 'canRemoveOperators')) {
    errors.push({
      field: 'operator',
      message: 'You do not have permission to remove operators',
      code: 'INSUFFICIENT_PERMISSIONS',
    });
  }

  // Find the operator
  const jobOperator = existingOperators.find(op => op.id === jobOperatorId);
  if (!jobOperator) {
    errors.push({
      field: 'operator',
      message: 'Job operator not found',
      code: 'OPERATOR_NOT_FOUND',
    });
  }

  // Check if operator has active time logs
  if (jobOperator && jobOperator.status === 'Active' && jobCard.status === 'In Progress') {
    errors.push({
      field: 'operator',
      message: 'Cannot remove operator with active time logs. Please pause the job first.',
      code: 'OPERATOR_HAS_ACTIVE_TIME',
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate file upload for attachments
 */
export function validateFileUpload(
  file: File,
  userRole: UserRole
): ValidationResult {
  const errors: ValidationError[] = [];

  // Check permission
  if (!hasPermission(userRole, 'canIssueAttachments')) {
    errors.push({
      field: 'file',
      message: 'You do not have permission to upload attachments',
      code: 'INSUFFICIENT_PERMISSIONS',
    });
  }

  // Check file size (10MB limit)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    errors.push({
      field: 'file',
      message: 'File size cannot exceed 10MB',
      code: 'FILE_TOO_LARGE',
    });
  }

  // Check file type
  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];

  if (!allowedTypes.includes(file.type)) {
    errors.push({
      field: 'file',
      message: 'File type not allowed. Please upload images, PDFs, or Office documents.',
      code: 'INVALID_FILE_TYPE',
    });
  }

  // Check filename length
  if (file.name.length > 255) {
    errors.push({
      field: 'file',
      message: 'Filename is too long (maximum 255 characters)',
      code: 'FILENAME_TOO_LONG',
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Create user-friendly error message from validation errors
 */
export function formatValidationErrors(errors: ValidationError[]): string {
  if (errors.length === 0) return '';
  
  if (errors.length === 1) {
    return errors[0].message;
  }

  return `Multiple validation errors:\n${errors.map(e => `• ${e.message}`).join('\n')}`;
}
